import { useMemo } from 'react';

import { useCheckSettings, useCurrent } from 'features/auth';
import { FEATURE_FLAG } from 'types/featureFlag';

import { TopNavbarDropdown } from './TopNavbarDropdown';
import { TopNavbarDropdownItemLink } from './TopNavbarDropdownItemLink';
import { TopNavbarDropdownItemRouterLink } from './TopNavbarDropdownItemRouterLink';

const REDIRECT_URL = process.env.REACT_APP_REDIRECT_URL;

export const TopNavbarOrders = () => {
  const [hasSettings] = useCheckSettings();
  const { data: currentData } = useCurrent();

  const isMigratedShiftListEnabled = hasSettings('migrated_shift_list_feature');
  const isMigratedOrderGroupListEnabled = hasSettings('migrated_order_group_list_feature');
  const isMigratedRecurringListEnabled = hasSettings('migrated_recurring_order_list_feature');
  const isMigratedLoadingOrderListEnabled = hasSettings('migrated_loading_order_list_feature');
  const isMigratedDeliveryOrderListEnabled = hasSettings('migrated_delivery_order_list_feature');
  const isMigratedTransferOrderListEnabled = hasSettings('migrated_transfer_order_list_feature');

  // Check if the recurring order feature is enabled in the current settings data.
  // Note: hasSettings always returns true for the recurring order feature, even if it is disabled,because recurring_order_feature is always present in userFeatureFlags.
  const isRecurringOrderFeatureEnabled =
    currentData?.current?.settings?.find(
      (indvSettingData) => indvSettingData?.name === FEATURE_FLAG.RECURRING_ORDER_FEATURE
    )?.value === 'true';

  const switchBetweenMigratedUrl = useMemo(
    () => ({
      delivery_order: isMigratedDeliveryOrderListEnabled
        ? '/orders/delivery'
        : `${REDIRECT_URL}main/orders/delivery`,
      shift_list: isMigratedShiftListEnabled
        ? '/orders/shift_list'
        : `${REDIRECT_URL}main/shifts/list`,
      loading_order: isMigratedLoadingOrderListEnabled
        ? '/orders/loading'
        : `${REDIRECT_URL}main/orders/loading`,
      transfer_order: isMigratedTransferOrderListEnabled
        ? '/orders/transfers'
        : `${REDIRECT_URL}main/orders/transfers`,
      recurring_order: isMigratedRecurringListEnabled
        ? '/orders/recurring'
        : `${REDIRECT_URL}main/orders/recurring`,
      order_group: isMigratedOrderGroupListEnabled
        ? '/orders/order_groups'
        : `${REDIRECT_URL}main/orders/order_groups`,
    }),
    [
      isMigratedShiftListEnabled,
      isMigratedRecurringListEnabled,
      isMigratedOrderGroupListEnabled,
      isMigratedLoadingOrderListEnabled,
      isMigratedDeliveryOrderListEnabled,
      isMigratedTransferOrderListEnabled,
    ]
  );

  return (
    <TopNavbarDropdown trigger="Orders">
      <TopNavbarDropdownItemLink href={switchBetweenMigratedUrl['shift_list']}>
        Shift List
      </TopNavbarDropdownItemLink>
      <TopNavbarDropdownItemRouterLink to={'/shift_boards/old/board'}>
        Shift Board
      </TopNavbarDropdownItemRouterLink>
      <TopNavbarDropdownItemRouterLink to={'/orders/weekly-shift-planner'}>
        Weekly Shift Planner
      </TopNavbarDropdownItemRouterLink>
      <TopNavbarDropdownItemLink href={switchBetweenMigratedUrl['order_group']}>
        Order Groups
      </TopNavbarDropdownItemLink>
      {hasSettings(FEATURE_FLAG.DELIVERY_ORDER_FEATURE) && (
        <TopNavbarDropdownItemRouterLink to="/orders/delivery">
          Delivery Orders
        </TopNavbarDropdownItemRouterLink>
      )}
      {hasSettings(FEATURE_FLAG.EXTRACTION_ORDER_FEATURE) && (
        <TopNavbarDropdownItemRouterLink to="/orders/extraction">
          Extraction Orders
        </TopNavbarDropdownItemRouterLink>
      )}
      {hasSettings(FEATURE_FLAG.LOADING_ORDER_FEATURE) && (
        <TopNavbarDropdownItemRouterLink to="/orders/loading">
          Loading Orders
        </TopNavbarDropdownItemRouterLink>
      )}
      {isRecurringOrderFeatureEnabled && (
        <TopNavbarDropdownItemLink href={switchBetweenMigratedUrl['recurring_order']}>
          Recurring Orders
        </TopNavbarDropdownItemLink>
      )}
      {currentData?.current?.customerSettings?.includes(FEATURE_FLAG.TRANSFER_ORDER_FEATURE) && (
        <TopNavbarDropdownItemLink href={switchBetweenMigratedUrl['transfer_order']}>
          Transfer Orders
        </TopNavbarDropdownItemLink>
      )}
      {hasSettings(FEATURE_FLAG.BULK_PLANT_SHIFT_AUTO_CREATE_FEATURE) && (
        <TopNavbarDropdownItemRouterLink to="/orders/bulk-plant">
          Bulk Plant
        </TopNavbarDropdownItemRouterLink>
      )}
      <TopNavbarDropdownItemRouterLink to={'/orders/driver_notes'}>
        Driver Notes
      </TopNavbarDropdownItemRouterLink>
      {hasSettings(FEATURE_FLAG.SHIFT_BUILDER_FEATURE) && (
        <TopNavbarDropdownItemRouterLink to={'/orders/route-builder'}>
          Route Builder
        </TopNavbarDropdownItemRouterLink>
      )}
      {hasSettings(FEATURE_FLAG.CARDLOCK_FEATURE) && (
        <>
          <TopNavbarDropdownItemRouterLink to="/orders/card-lock">
            Card Lock
          </TopNavbarDropdownItemRouterLink>
          <TopNavbarDropdownItemRouterLink to="orders/invoices">
            Invoices
          </TopNavbarDropdownItemRouterLink>
        </>
      )}
    </TopNavbarDropdown>
  );
};
